import * as React from "react";
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as AccountsState from "../../store/Accounts";
import { PageControl } from "./PageControl";
import { debounce } from "lodash";
import { defaultPageSize } from "../../helpers";
import { AccountResponse } from "../../api/ApiClient";
import { Spinner } from "./Spinner";
import SearchInput from "./SearchInput";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import { AdminTable } from "./AdminTable";

interface Injected {
  accounts: AccountsState.AccountsState;
}

type OwnProps = {
  onSelected: (site: AccountResponse) => any;
  pageSize?: number;
  selectedAccounts?: Array<AccountResponse>;
  renderButton?: (site: AccountResponse) => any;
  selectedLabel?: string;
  selectedBtnClass?: string;
  unselectedLabel?: string;
  unselectedBtnClass?: string;
  btnStyle?: Object;
};

interface SnackProps {
  enqueueSnackbar: (node: React.ReactNode, options?: any) => void;
  closeSnackbar: (key?: any) => void;
}

type Props = Injected &
  typeof AccountsState.actionCreators &
  OwnProps &
  SnackProps;

interface State {
  typedAccount: string;
}

export class SelectAccount extends React.Component<Props, State> {
  state: State = {
    typedAccount: "",
  };

  componentDidMount() {
    console.log("SelectAccount: Requesting accounts...");
    this.refresh();
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.pageSize !== prevProps.pageSize) {
      this.refresh();
    }
  }

  refresh = async () => {
    try {
      const { pageSize } = this.props;
      const { typedAccount: searchTerm } = this.state;
      await this.props.setFetchParamAccounts({
        page: 0,
        pageSize: pageSize || defaultPageSize,
        searchTerm,
      });
    } catch (e) {
      console.error("Error requesting accounts", e);
      let errorMessage = "Error requesting accounts";
      try {
        const err: any = e;
        if (err?.response) {
          const data =
            typeof err.response === "string"
              ? JSON.parse(err.response)
              : err.response;
          if (data?.errors?.length) {
            errorMessage = data.errors.map((x: any) => x.message).join(", ");
          } else if (data?.message) {
            errorMessage = data.message;
          }
        } else if (err?.message) {
          errorMessage = err.message;
        }
      } catch {
        // ignore parse errors
      }
      this.props.enqueueSnackbar(
        <span style={{ fontSize: 12 }}>{errorMessage}</span>,
        { variant: "error" },
      );
    }
  };

  handleTypedAccount = (value: string) => {
    this.setState({ typedAccount: value }, this.searchAccount);
  };

  searchAccount = debounce(this.refresh, 1000);

  public render() {
    const { accounts, searchTerm, isLoading } = this.props.accounts;

    const { typedAccount } = this.state;

    return (
      <div>
        <div className="flex justify-end">
          <SearchInput
            id="typedAccount"
            name="typedAccount"
            placeholder="Search here"
            value={typedAccount}
            onChange={(e) => this.handleTypedAccount(e.target.value)}
          />
        </div>
        <h4>
          {searchTerm ? `Accounts Matching '${searchTerm}'` : ""}
          {isLoading && <Spinner />}
        </h4>
        {this.renderAccountsTable(accounts)}
      </div>
    );
  }

  private defaultRenderButton = (account: AccountResponse) => {
    const {
      selectedAccounts,
      onSelected,
      btnStyle,
      selectedBtnClass,
      selectedLabel,
      unselectedBtnClass,
      unselectedLabel,
    } = this.props;
    const isSelected =
      selectedAccounts && selectedAccounts.find((a) => a.id === account.id);
    const className = isSelected
      ? selectedBtnClass || "btn-primary"
      : unselectedBtnClass || "btn-default";
    return (
      <Button
        className={`btn ${className}`}
        style={btnStyle as React.CSSProperties | undefined}
        onClick={() => onSelected(account)}
        type="button"
        variant={isSelected ? "default" : "outline"}
      >
        {!isSelected
          ? unselectedLabel || "Select"
          : selectedLabel || "Unselect"}
      </Button>
    );
  };

  private renderAccountsTable(accounts: AccountResponse[]) {
    const { renderButton } = this.props;
    const { isLoading } = this.props.accounts;

    return (
      <div>
        <AdminTable aria-label="accounts table">
          <thead>
            <tr>
              <th className="w-[110px]" />
              <th>Id</th>
              <th>Name</th>
              <th>System Type</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id}>
                <td className="w-[110px]">
                  {renderButton
                    ? renderButton(account)
                    : this.defaultRenderButton(account)}
                </td>
                <td>{account.id}</td>
                <td>{account.name}</td>
                <td>{account.systemType}</td>
              </tr>
            ))}
            {(!accounts || accounts.length === 0) && !isLoading && (
              <tr>
                <td className="text-center" colSpan={4}>
                  No accounts found
                </td>
              </tr>
            )}
          </tbody>
        </AdminTable>
        <div className="mt-4">
          <PageControl
            onNext={this.props.nextAccountPage}
            onPrev={this.props.prevAccountPage}
            currentPage={this.props.accounts.page}
            isLoading={isLoading}
            disablePrev={this.props.accounts.page === 0}
            disableNext={
              this.props.accounts.accounts.length < this.props.accounts.pageSize
            }
          />
        </div>
      </div>
    );
  }
}

const ConnectedSelectAccount = connect<any, any, OwnProps, ApplicationState>(
  (state) => ({
    accounts: state.accounts,
  }),
  { ...AccountsState.actionCreators },
)(SelectAccount);

const SelectAccountWithSnackbar: React.FC<any> = (props) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  return (
    <ConnectedSelectAccount
      {...props}
      enqueueSnackbar={enqueueSnackbar}
      closeSnackbar={closeSnackbar}
    />
  );
};

export default SelectAccountWithSnackbar;
